<template>
  <figure
    v-if="image"
    class="dato-asset"
    ref="figureElement"
    :style="{
      '--background-color': getBackgroundColor,
      '--scale': scale,
      '--aspect-ratio': aspectRatio
    }"
    :class="{ '-fade-in': fadeIn !== 'none', '-in-viewport': isElementVisible }"
  >
    <Image
      :alt="asset.alt ?? ''"
      class="responsiveimage image"
      v-if="image.responsiveImage"
      :data="image.responsiveImage as ResponsiveImageType"
      :lazy-load="lazyLoad"
      :aspectRatio="aspectRatio"
      ref="imageElement"
    />
    <VideoPlayer v-else-if="asset.video" class="video" :data="asset.video" />
    <figcaption class="caption" v-if="caption">{{ caption }}</figcaption>
    <footer
      v-show="!hideCopyright"
      v-if="copyright"
      :class="{ '-open': isCopyrightOpen }"
      :style="{ top: `${copyrightPositionTop}px` }"
      @click="toggleCopyright()"
      class="footer"
    >
      <small class="copyright">
        <span class="inner">{{ copyright }}</span>
      </small>
      <button class="toggle" type="button" aria-label="Toggle"></button>
    </footer>
  </figure>
</template>

<script setup lang="ts">
import { Image, type ResponsiveImageType } from 'vue-datocms';
import VideoPlayer from '~/components/shared/VideoPlayer.vue';
import useIntersectionObserver from '~/utils/useIntersectionObserver';
import useImageScaleForHover from '~/utils/useImageScaleForHover';
import type { DatoAssetDto } from '~/dato/dato';
import { useCopyright } from '~/utils/useCopyright';

const props = defineProps<{
  caption?: string;
  fadeIn?: 'immediate' | 'on-intersection' | 'none';
  backgroundColor?: string;
  asset: DatoAssetDto;
  assetVs?: DatoAssetDto;
  aspectRatio?: string;
  lazyLoad?: boolean;
  hideCopyright?: boolean;
}>();

const fadeIn = props.fadeIn ?? 'none';
const figureElement = ref<HTMLElement | null>(null);
const imageElement = ref<HTMLElement | null>(null);
const viewport = ref<'vs' | 'vl'>('vl');

const { isElementVisible, start } = useIntersectionObserver(figureElement);
const { scale } = useImageScaleForHover(figureElement);
const {
  toggle: toggleCopyright,
  isOpen: isCopyrightOpen,
  top: copyrightPositionTop
} = useCopyright(imageElement);

if (fadeIn === 'none') {
  isElementVisible.value = true;
}

const getBackgroundColor = computed(function () {
  if (props.asset?.customData?.Color) {
    return props.asset.customData.Color;
  }

  if (props.asset?.colors?.length && props.asset.colors[0].hex) {
    return props.asset.colors[0]?.hex;
  }

  return props.backgroundColor ?? '#e6e6e6';
});

const copyright = computed(function () {
  return props.asset?.copyright ?? null;
});

const runtimeConfig = useRuntimeConfig();
const { width } = useWindowSize();

const image = computed(() => {
  if (width.value < runtimeConfig.public.mobileBreakpoint && props.assetVs) {
    viewport.value = 'vs';

    return props.assetVs;
  }
  viewport.value = 'vl';

  return props.asset;
});

onMounted(function () {
  // add animation
  if (fadeIn === 'on-intersection') {
    start();
  } else if (fadeIn === 'immediate') {
    isElementVisible.value = true;
  }
});
</script>

<style scoped lang="scss">
.dato-asset {
  --scale: 1;

  position: relative;
  // width: var(--width, 100%);
  // height: var(--height, 100%);

  @media (--vs) {
    --transition-delay: 0 !important;
  }

  @media (--vs) {
    --transition-delay: 150ms;
  }

  &.-hover-enabled {
    transition: clip-path 500ms cubic-bezier(0.4, 0, 0.2, 1);
    scale: var(--scale);
    clip-path: inset(5px 5px 5px 5px);

    @media (hover: hover) {
      &:hover {
        clip-path: inset(0% 0% 0% 0%);

        > .caption {
          transform: translateY(-5px);
        }
      }
    }
  }

  &.-fade-in {
    &.-in-viewport {
      &::after {
        clip-path: inset(0% 0% 100% 0%);
      }
    }

    &:not(.-in-viewport) {
      &::after {
        clip-path: inset(0% 0% 0% 0%);
      }
    }

    &::after {
      content: '';
      background-color: color-mix(in srgb, var(--background-color) 20%, white);
      position: absolute;
      inset: -1px;
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      transition: clip-path 800ms cubic-bezier(0.4, 0, 0.2, 1);
      transition-delay: var(--transition-delay);
    }
  }

  > .placeholder,
  > .responsiveimage,
  > .image,
  > .video {
    width: 100%;
    height: 100%;
    aspect-ratio: var(--aspect-ratio);
  }

  > .responsiveimage {
    width: 100%;
    height: 100%;
    max-width: unset !important;

    :deep(> img) {
      width: 100%;
      height: 100%;
    }

    :deep(picture) {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      img {
        object-fit: cover;
      }
    }
  }

  > .placeholder {
    background-color: var(--color-grey);
  }

  > .video {
    width: 100%;
    height: 100%;
  }

  > .caption {
    @include text-sm(700);

    padding: 1rem 0 0 0;
    display: block;
    transition: transform 500ms;
  }

  > .footer {
    position: absolute;
    right: 1rem;
    top: 0;
    transform: translateY(calc(-100% - 1rem));
    z-index: 10;
    display: flex;
    justify-content: flex-end;

    &.-open,
    &:hover {
      > .copyright {
        width: 100%;
        background-color: var(--color-black);

        > .inner {
          transform: translate(0, -1px);
          opacity: 1;
          transition-delay: var(--delay);
          transition-duration: var(--duration);
        }
      }

      > .toggle {
        opacity: 1;
      }
    }

    > .copyright {
      background-color: transparent;
      color: var(--color-white);
      border-radius: 1.5rem;
      padding: 0 3rem 0 1.25rem;
      height: 2.3125rem;
      display: flex;
      align-items: center;
      //clip-path: inset(0% 0% 0% 100%);
      width: 1.5rem;
      transition:
        width 500ms 0ms,
        background-color 500ms 0ms;
      overflow: clip;
      transform-origin: right center;

      > .inner {
        --duration: 300ms;
        --delay: 600ms;

        @include text-xs(300);

        transform: translate(1rem, -1px);
        opacity: 0;
        transition:
          transform 0ms,
          opacity 0ms;
      }
    }

    > .toggle {
      width: 1.5625rem;
      height: 1.5625rem;
      border-radius: 1.81163rem;
      outline: none;
      border: none;
      background-image: url('/copyright-toggle.svg');
      background-size: cover;
      position: absolute;
      inset: 0.38rem 0.38rem 0.38rem auto;
      cursor: pointer;
      opacity: 0.8;
      transition: opacity 500ms 0ms;
    }
  }
}
</style>
